// Filial kassasi — xsib-payments `/api/cashdesk/**` (ADR-039).
// Server `non_null` bilan javob beradi: bo'sh maydon umuman kelmaydi, shuning uchun `.nullish()`.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { listParams } from '@/shared/api/serverPage'
import { instantSchema, moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { TRANSFER_TYPE_CODE } from '@/features/cashdesk/transferTypeApi'

export const RECEIPT_KINDS = ['PAYMENT', 'REVERSAL'] as const
export const RECEIPT_STATUSES = ['COMPLETED', 'PARTIAL'] as const
export const RECEIPT_PURPOSES = ['TOPUP', 'SERVICE'] as const
export const RECEIPT_ITEM_KINDS = ['TOPUP', 'PACKAGE', 'SERVICE'] as const
export const PACKAGE_PURCHASE_STATUSES = ['NONE', 'ISSUED', 'FAILED'] as const
export const TRANSFER_STATES = ['SENT', 'CONFIRMED', 'REJECTED'] as const
export const ENTRY_KINDS = ['OTHER_INCOME', 'EXPENSE', 'ADJUSTMENT_SHORT', 'ADJUSTMENT_OVER'] as const
export const ENTRY_STATES = ['PENDING', 'APPROVED', 'REJECTED', 'REVERSED'] as const
export const DOCUMENT_TYPES = ['RECEIPT', 'TRANSFER', 'ENTRY'] as const
export const ORG_NODE_STATES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const

export type ReceiptPurpose = (typeof RECEIPT_PURPOSES)[number]
export type TransferState = (typeof TRANSFER_STATES)[number]
export type EntryKind = (typeof ENTRY_KINDS)[number]
export type EntryState = (typeof ENTRY_STATES)[number]

export const REASON_MAX = 500
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
export const ATTACHMENT_ACCEPT = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  '.docx',
  '.xlsx',
].join(',')

export const branchRefSchema = z.object({ id: uuidSchema, code: z.string(), name: z.string() })
export const actorRefSchema = z.object({ id: uuidSchema, name: z.string() })

export type BranchRef = z.infer<typeof branchRefSchema>
export type ActorRef = z.infer<typeof actorRefSchema>

export const pageOf = <T extends z.ZodTypeAny>(row: T) =>
  z.object({ rows: z.array(row), page: z.number(), size: z.number(), hasNext: z.boolean() })

// --- Filiallar ---------------------------------------------------------------

const branchSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  state: z.enum(ORG_NODE_STATES),
  cash: moneySchema,
  inTransit: moneySchema,
  asOf: instantSchema.nullish(),
})

export interface CashBranch {
  id: string
  code: string
  name: string
  state: (typeof ORG_NODE_STATES)[number]
  cash: Money
  inTransit: Money
  asOf: string | null
}

function toBranch(raw: z.infer<typeof branchSchema>): CashBranch {
  return {
    ...raw,
    cash: toMoney(raw.cash),
    inTransit: toMoney(raw.inTransit),
    asOf: raw.asOf ?? null,
  }
}

export async function fetchCashBranches(signal?: AbortSignal): Promise<CashBranch[]> {
  const response = await api.get<unknown>('/api/cashdesk/branches', { ...(signal ? { signal } : {}) })
  return z.array(branchSchema).parse(response.data).map(toBranch)
}

const branchPeriodSchema = z.object({
  id: z.string(),
  opening: moneySchema,
  received: moneySchema,
  paidOut: moneySchema,
  closing: moneySchema,
})

export interface BranchPeriod {
  id: string
  opening: Money
  received: Money
  paidOut: Money
  closing: Money
}

/** Filial kassasi davr bo'yicha; `from` bo'sh — butun davr, `branchIds` bo'sh — ko'rinadigan hamma filial */
const flowSchema = z.object({
  opening: moneySchema,
  income: moneySchema,
  received: moneySchema,
  expenses: moneySchema,
  transferred: moneySchema,
  other: moneySchema,
  closing: moneySchema,
})

const cashFlowSchema = z.object({
  branches: z.array(z.object({ id: uuidSchema, code: z.string(), name: z.string(), state: z.string(), flow: flowSchema })),
  months: z.array(z.object({ month: z.string(), flow: flowSchema })),
  total: flowSchema,
})

export interface CashFlowFigures {
  opening: Money
  income: Money
  /** Topshirish orqali kirgan naqd — bosh ofisda filiallardan, filialda rad etilib qaytgani */
  received: Money
  expenses: Money
  /** Bosh ofisga topshirilgan */
  transferred: Money
  other: Money
  closing: Money
}

export interface CashFlowReport {
  branches: readonly ({ id: string; code: string; name: string; state: string } & CashFlowFigures)[]
  months: readonly ({ month: string } & CashFlowFigures)[]
  total: CashFlowFigures
}

function toFlow(raw: z.infer<typeof flowSchema>): CashFlowFigures {
  return {
    opening: toMoney(raw.opening),
    income: toMoney(raw.income),
    received: toMoney(raw.received),
    expenses: toMoney(raw.expenses),
    transferred: toMoney(raw.transferred),
    other: toMoney(raw.other),
    closing: toMoney(raw.closing),
  }
}

/** Filiallar kassasi oqimi — har filial va oyma-oy (bosh ofis moliya paneli) */
export async function fetchCashFlow(
  branchIds: readonly string[],
  from: string | null,
  to: string | null,
  signal?: AbortSignal,
): Promise<CashFlowReport> {
  const params = new URLSearchParams()
  branchIds.forEach((id) => params.append('branchId', id))
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  const response = await api.get<unknown>('/api/cashdesk/flow', { ...(signal ? { signal } : {}), params })
  const raw = cashFlowSchema.parse(response.data)
  return {
    branches: raw.branches.map(({ flow, ...branch }) => ({ ...branch, ...toFlow(flow) })),
    months: raw.months.map(({ month, flow }) => ({ month, ...toFlow(flow) })),
    total: toFlow(raw.total),
  }
}

export async function fetchBranchPeriod(
  branchIds: readonly string[],
  from: string | null,
  to: string | null,
  signal?: AbortSignal,
): Promise<BranchPeriod[]> {
  const params = new URLSearchParams()
  branchIds.forEach((id) => params.append('branchId', id))
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  const response = await api.get<unknown>('/api/cashdesk/period', { ...(signal ? { signal } : {}), params })
  return z
    .array(branchPeriodSchema)
    .parse(response.data)
    .map((raw) => ({
      id: raw.id,
      opening: toMoney(raw.opening),
      received: toMoney(raw.received),
      paidOut: toMoney(raw.paidOut),
      closing: toMoney(raw.closing),
    }))
}

export async function fetchCashBalance(branchId: string | null, signal?: AbortSignal): Promise<CashBranch> {
  const response = await api.get<unknown>('/api/cashdesk/balance', {
    ...(signal ? { signal } : {}),
    params: branchId ? { branchId } : {},
  })
  return toBranch(branchSchema.parse(response.data))
}

const historyRowSchema = z.object({
  entryId: z.number(),
  txnId: z.number(),
  at: instantSchema,
  txnKind: z.string(),
  amount: moneySchema,
  document: z
    .object({
      type: z.enum(DOCUMENT_TYPES),
      id: uuidSchema,
      number: z.string().nullish(),
      transferType: z.string().nullish(),
      externalReference: z.string().nullish(),
    })
    .nullish(),
})

export type DocumentType = (typeof DOCUMENT_TYPES)[number]

export interface CashDocument {
  type: DocumentType
  id: string
  number: string | null
  transferType: string | null
  externalReference: string | null
}

export interface CashHistoryRow {
  entryId: number
  txnId: number
  at: string
  txnKind: string
  amount: Money
  document: CashDocument | null
}

export async function fetchCashHistory(
  branchId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<CashHistoryRow[]> {
  const response = await api.get<unknown>('/api/cashdesk/history', {
    ...(signal ? { signal } : {}),
    params: { branchId, from, to },
  })
  return z
    .array(historyRowSchema)
    .parse(response.data)
    .map((raw) => ({
      ...raw,
      amount: toMoney(raw.amount),
      document: raw.document
        ? {
            ...raw.document,
            number: raw.document.number ?? null,
            transferType: raw.document.transferType ?? null,
            externalReference: raw.document.externalReference ?? null,
          }
        : null,
    }))
}

// --- Cheklar -----------------------------------------------------------------

const receiptItemSchema = z.object({
  kind: z.enum(RECEIPT_ITEM_KINDS),
  name: z.string().nullish(),
  amount: moneySchema,
  planId: uuidSchema.nullish(),
  grantId: uuidSchema.nullish(),
  category: z.string().nullish(),
})

export const DISCOUNT_DECISIONS = ['AUTO', 'APPROVAL'] as const
export type DiscountDecision = (typeof DISCOUNT_DECISIONS)[number]

const receiptSaleSchema = z.object({
  planId: uuidSchema.nullish(),
  planName: z.string().nullish(),
  price: moneySchema,
  paid: moneySchema,
  discount: moneySchema.nullish(),
  discountId: uuidSchema.nullish(),
  discountDecision: z.enum(DISCOUNT_DECISIONS).nullish(),
  discountApprovedBy: z.string().nullish(),
  discountReason: z.string().nullish(),
  discountState: z.string().nullish(),
  balanceBefore: moneySchema,
  balanceAfter: moneySchema,
  outstanding: moneySchema,
  creditLimitBefore: moneySchema.nullish(),
  creditLimitAfter: moneySchema.nullish(),
})

export interface ReceiptSale {
  planId: string | null
  planName: string | null
  price: Money
  paid: Money
  discount: Money | null
  discountId: string | null
  discountDecision: DiscountDecision | null
  discountApprovedBy: string | null
  discountReason: string | null
  discountState: string | null
  balanceBefore: Money
  balanceAfter: Money
  outstanding: Money
  creditLimitBefore: Money | null
  creditLimitAfter: Money | null
}

function toReceiptSale(raw: z.infer<typeof receiptSaleSchema>): ReceiptSale {
  return {
    planId: raw.planId ?? null,
    planName: raw.planName ?? null,
    price: toMoney(raw.price),
    paid: toMoney(raw.paid),
    discount: raw.discount ? toMoney(raw.discount) : null,
    discountId: raw.discountId ?? null,
    discountDecision: raw.discountDecision ?? null,
    discountApprovedBy: raw.discountApprovedBy ?? null,
    discountReason: raw.discountReason ?? null,
    discountState: raw.discountState ?? null,
    balanceBefore: toMoney(raw.balanceBefore),
    balanceAfter: toMoney(raw.balanceAfter),
    outstanding: toMoney(raw.outstanding),
    creditLimitBefore: raw.creditLimitBefore ? toMoney(raw.creditLimitBefore) : null,
    creditLimitAfter: raw.creditLimitAfter ? toMoney(raw.creditLimitAfter) : null,
  }
}

export const receiptSchema = z.object({
  id: uuidSchema,
  kind: z.enum(RECEIPT_KINDS),
  status: z.enum(RECEIPT_STATUSES),
  purpose: z.enum(RECEIPT_PURPOSES),
  receiptNo: z.string(),
  reversesId: uuidSchema.nullish(),
  reversesReceiptNo: z.string().nullish(),
  reversedBy: z.object({ id: uuidSchema, receiptNo: z.string() }).nullish(),
  amount: moneySchema,
  method: z.string(),
  createdAt: instantSchema,
  branch: branchRefSchema,
  cashier: actorRefSchema,
  customer: z.object({
    subscriberId: uuidSchema,
    customerId: z.string().nullish(),
    login: z.string(),
    fullName: z.string().nullish(),
    phone: z.string().nullish(),
  }),
  items: z.array(receiptItemSchema),
  ledgerTxnId: z.number(),
  reference: z.string(),
  reason: z.string().nullish(),
  company: z
    .object({
      name: z.string().nullish(),
      address: z.string().nullish(),
      phone: z.string().nullish(),
      email: z.string().nullish(),
      website: z.string().nullish(),
      logoUrl: z.string().nullish(),
    })
    .nullish(),
  printCount: z.number(),
  sale: receiptSaleSchema.nullish(),
  invoice: z.object({ id: uuidSchema, invoiceNo: z.string().nullish() }).nullish(),
})

export interface ReceiptItem {
  kind: (typeof RECEIPT_ITEM_KINDS)[number]
  name: string | null
  amount: Money
  planId: string | null
  grantId: string | null
  category: string | null
}

export interface ReceiptCompany {
  name: string | null
  address: string | null
  phone: string | null
  email: string | null
  website: string | null
  logoUrl: string | null
}

export interface Receipt {
  id: string
  kind: (typeof RECEIPT_KINDS)[number]
  status: (typeof RECEIPT_STATUSES)[number]
  purpose: ReceiptPurpose
  receiptNo: string
  reversesId: string | null
  reversesReceiptNo: string | null
  reversedBy: { id: string; receiptNo: string } | null
  amount: Money
  method: string
  createdAt: string
  branch: BranchRef
  cashier: ActorRef
  customer: {
    subscriberId: string
    customerId: string | null
    login: string
    fullName: string | null
    phone: string | null
  }
  items: ReceiptItem[]
  ledgerTxnId: number
  reference: string
  reason: string | null
  company: ReceiptCompany
  printCount: number
  sale: ReceiptSale | null
  invoice: { id: string; invoiceNo: string | null } | null
}

export function toReceipt(raw: z.infer<typeof receiptSchema>): Receipt {
  const company = raw.company ?? {}
  return {
    ...raw,
    reversesId: raw.reversesId ?? null,
    reversesReceiptNo: raw.reversesReceiptNo ?? null,
    reversedBy: raw.reversedBy ?? null,
    amount: toMoney(raw.amount),
    customer: {
      subscriberId: raw.customer.subscriberId,
      customerId: raw.customer.customerId ?? null,
      login: raw.customer.login,
      fullName: raw.customer.fullName ?? null,
      phone: raw.customer.phone ?? null,
    },
    items: raw.items.map((item) => ({
      kind: item.kind,
      name: item.name ?? null,
      amount: toMoney(item.amount),
      planId: item.planId ?? null,
      grantId: item.grantId ?? null,
      category: item.category ?? null,
    })),
    reason: raw.reason ?? null,
    company: {
      name: company.name ?? null,
      address: company.address ?? null,
      phone: company.phone ?? null,
      email: company.email ?? null,
      website: company.website ?? null,
      logoUrl: company.logoUrl ?? null,
    },
    sale: raw.sale ? toReceiptSale(raw.sale) : null,
    invoice: raw.invoice ? { id: raw.invoice.id, invoiceNo: raw.invoice.invoiceNo ?? null } : null,
  }
}

export interface ReceiptFilter {
  subscriberId?: string
  branchId?: string
  mine?: boolean
  day?: string
}

function receiptParams(filter: ReceiptFilter): Record<string, string> {
  return {
    ...(filter.subscriberId ? { subscriberId: filter.subscriberId } : {}),
    ...(filter.branchId ? { branchId: filter.branchId } : {}),
    ...(filter.mine ? { cashier: 'me' } : {}),
    ...(filter.day ? { day: filter.day } : {}),
  }
}

export function fetchReceiptPage(
  filter: ReceiptFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<Receipt>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/cashdesk/receipts', {
      signal,
      params: { ...receiptParams(filter), page: state.page - 1, size: state.pageSize },
    })
    const parsed = pageOf(receiptSchema).parse(response.data)
    return {
      items: parsed.rows.map(toReceipt),
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

export async function fetchReceipts(
  filter: ReceiptFilter,
  size: number,
  signal?: AbortSignal,
): Promise<Receipt[]> {
  const response = await api.get<unknown>('/api/cashdesk/receipts', {
    ...(signal ? { signal } : {}),
    params: { ...receiptParams(filter), page: 0, size },
  })
  return pageOf(receiptSchema).parse(response.data).rows.map(toReceipt)
}

export async function fetchReceipt(id: string): Promise<Receipt> {
  const response = await api.get<unknown>(`/api/cashdesk/receipts/${id}`)
  return toReceipt(receiptSchema.parse(response.data))
}

export async function recordReceiptPrint(id: string): Promise<{ copy: boolean; printNo: number }> {
  const response = await api.post<unknown>(`/api/cashdesk/receipts/${id}/prints`)
  return z.object({ copy: z.boolean(), printNo: z.number() }).parse(response.data)
}

export interface ReceiptDraft {
  subscriberId: string
  branchId: string | null
  purpose: ReceiptPurpose
  amount: number
  planId: string | null
  serviceName: string | null
  discount?: number | null
  discountReason?: string | null
  discountId?: string | null
  allowCredit?: boolean
  invoiceId?: string | null
  lines?: readonly ReceiptLineDraft[]
}

export interface ReceiptLineDraft {
  categoryId: string
  amount: number
}

const receiptResultSchema = z.object({
  receipt: receiptSchema,
  replay: z.boolean(),
  packagePurchase: z.object({
    status: z.enum(PACKAGE_PURCHASE_STATUSES),
    planId: uuidSchema.nullish(),
    grantId: uuidSchema.nullish(),
    errorTag: z.string().nullish(),
    errorText: z.string().nullish(),
    retryKey: z.string().nullish(),
  }),
})

export interface ReceiptResult {
  receipt: Receipt
  replay: boolean
  packagePurchase: {
    status: (typeof PACKAGE_PURCHASE_STATUSES)[number]
    planId: string | null
    grantId: string | null
    errorTag: string | null
    errorText: string | null
    retryKey: string | null
  }
}

export async function createReceipt(draft: ReceiptDraft, idempotencyKey: string): Promise<ReceiptResult> {
  const response = await api.post<unknown>(
    '/api/cashdesk/receipts',
    {
      subscriberId: draft.subscriberId,
      ...(draft.branchId ? { branchId: draft.branchId } : {}),
      purpose: draft.purpose,
      amount: draft.amount,
      method: 'CASH',
      ...(draft.purpose === 'TOPUP' && draft.planId ? { planId: draft.planId } : {}),
      ...(draft.purpose === 'SERVICE' && draft.serviceName ? { serviceName: draft.serviceName } : {}),
      ...(draft.discount ? { discount: draft.discount } : {}),
      ...(draft.discount && draft.discountReason ? { discountReason: draft.discountReason } : {}),
      ...(draft.discountId ? { discountId: draft.discountId } : {}),
      ...(draft.allowCredit ? { allowCredit: true } : {}),
      ...(draft.invoiceId ? { invoiceId: draft.invoiceId } : {}),
      ...(draft.lines?.length ? { lines: draft.lines } : {}),
    },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  const parsed = receiptResultSchema.parse(response.data)
  const purchase = parsed.packagePurchase
  return {
    receipt: toReceipt(parsed.receipt),
    replay: parsed.replay,
    packagePurchase: {
      status: purchase.status,
      planId: purchase.planId ?? null,
      grantId: purchase.grantId ?? null,
      errorTag: purchase.errorTag ?? null,
      errorText: purchase.errorText ?? null,
      retryKey: purchase.retryKey ?? null,
    },
  }
}

export async function reverseReceipt(id: string, reason: string): Promise<Receipt> {
  const response = await api.post<unknown>(`/api/cashdesk/receipts/${id}/reverse`, { reason })
  return toReceipt(receiptSchema.parse(response.data))
}

// --- Abonent qidiruvi (subscriber.directory) -----------------------------------

const directoryRowSchema = z.object({
  id: uuidSchema,
  login: z.string(),
  displayName: z.string().nullish(),
  phone: z.string().nullish(),
  planName: z.string().nullish(),
  balance: moneySchema.nullish(),
  state: z.string(),
  customerId: z.string().nullish(),
  branchNodeId: z.string().nullish(),
})

export interface DirectorySubscriber {
  id: string
  login: string
  displayName: string | null
  phone: string | null
  planName: string | null
  balance: Money | null
  state: string
  customerId: string | null
  branchNodeId: string | null
}

export const DIRECTORY_LIMIT = 20

export async function searchDirectory(search: string, signal?: AbortSignal): Promise<DirectorySubscriber[]> {
  const response = await api.get<unknown>('/api/subscribers/directory', {
    ...(signal ? { signal } : {}),
    params: { search, page: 0, size: DIRECTORY_LIMIT },
  })
  const parsed = z.object({ rows: z.array(directoryRowSchema) }).parse(response.data)
  return parsed.rows.map((raw) => ({
    id: raw.id,
    login: raw.login,
    displayName: raw.displayName ?? null,
    phone: raw.phone ?? null,
    planName: raw.planName ?? null,
    balance: raw.balance ? toMoney(raw.balance) : null,
    state: raw.state,
    customerId: raw.customerId ?? null,
    branchNodeId: raw.branchNodeId ?? null,
  }))
}

// --- Ilovalar ----------------------------------------------------------------

export type AttachmentOwner = 'transfers' | 'entries'

const attachmentSchema = z.object({
  id: uuidSchema,
  fileName: z.string().nullish(),
  contentType: z.string(),
  sizeBytes: z.number(),
  createdAt: instantSchema,
  createdBy: actorRefSchema,
})

export interface CashAttachment {
  id: string
  fileName: string | null
  contentType: string
  sizeBytes: number
  createdAt: string
  createdBy: ActorRef
}

const attachmentBase = (owner: AttachmentOwner, id: string) => `/api/cashdesk/${owner}/${id}/attachments`

export async function fetchAttachments(
  owner: AttachmentOwner,
  id: string,
  signal?: AbortSignal,
): Promise<CashAttachment[]> {
  const response = await api.get<unknown>(attachmentBase(owner, id), { ...(signal ? { signal } : {}) })
  return z
    .array(attachmentSchema)
    .parse(response.data)
    .map((raw) => ({ ...raw, fileName: raw.fileName ?? null }))
}

export async function uploadAttachment(owner: AttachmentOwner, id: string, file: File): Promise<CashAttachment> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<unknown>(attachmentBase(owner, id), form, {
    headers: { 'Content-Type': undefined },
  })
  const raw = attachmentSchema.parse(response.data)
  return { ...raw, fileName: raw.fileName ?? null }
}

export async function attachmentUrl(owner: AttachmentOwner, id: string, attachmentId: string): Promise<string> {
  const response = await api.get<unknown>(`${attachmentBase(owner, id)}/${attachmentId}/url`)
  return z.object({ url: z.string() }).parse(response.data).url
}

// --- Topshirishlar -----------------------------------------------------------

const transferSchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  state: z.enum(TRANSFER_STATES),
  fromBranch: branchRefSchema,
  toNode: branchRefSchema,
  amount: moneySchema,
  responsiblePerson: z.string(),
  remarks: z.string().nullish(),
  sentBy: actorRefSchema,
  sentAt: instantSchema,
  decidedBy: actorRefSchema.nullish(),
  decidedAt: instantSchema.nullish(),
  decisionNote: z.string().nullish(),
  sendTxnId: z.number(),
  decisionTxnId: z.number().nullish(),
  attachmentCount: z.number(),
  transferType: z.string(),
  externalReference: z.string().nullish(),
})

export interface CashTransfer {
  id: string
  reference: string
  state: TransferState
  fromBranch: BranchRef
  toNode: BranchRef
  amount: Money
  responsiblePerson: string
  remarks: string | null
  sentBy: ActorRef
  sentAt: string
  decidedBy: ActorRef | null
  decidedAt: string | null
  decisionNote: string | null
  sendTxnId: number
  decisionTxnId: number | null
  attachmentCount: number
  transferType: string
  externalReference: string | null
}

function toTransfer(raw: z.infer<typeof transferSchema>): CashTransfer {
  return {
    ...raw,
    amount: toMoney(raw.amount),
    remarks: raw.remarks ?? null,
    decidedBy: raw.decidedBy ?? null,
    decidedAt: raw.decidedAt ?? null,
    decisionNote: raw.decisionNote ?? null,
    decisionTxnId: raw.decisionTxnId ?? null,
    externalReference: raw.externalReference ?? null,
  }
}

export interface ListFilter<S extends string> {
  branchId: string | null
  state: S | null
}

/** Ustun kaliti → `GET /api/cashdesk/transfers` ning `sortField` enumi */
export const TRANSFER_SORT_FIELDS = {
  sentAt: 'SENT_AT',
  amount: 'AMOUNT',
  reference: 'REFERENCE',
  state: 'STATE',
  transferType: 'TRANSFER_TYPE',
} as const

/** Ustun kaliti → `GET /api/cashdesk/entries` ning `sortField` enumi */
export const ENTRY_SORT_FIELDS = {
  requestedAt: 'REQUESTED_AT',
  amount: 'AMOUNT',
  kind: 'KIND',
  state: 'STATE',
} as const

/** URL filtri kalitlari — `f.<kalit>` */
export type TransferFilterKey = 'branchId' | 'state' | 'transferType' | 'from' | 'to'

function pick<T extends string>(values: readonly string[] | undefined, allowed: readonly T[]): T | undefined {
  return allowed.find((value) => value === values?.[0])
}

function transferTypeCode(values: readonly string[] | undefined): string | undefined {
  const raw = values?.[0]
  return raw !== undefined && TRANSFER_TYPE_CODE.test(raw) ? raw : undefined
}

function instant(values: readonly string[] | undefined): string | undefined {
  const raw = values?.[0]
  return raw === undefined || Number.isNaN(Date.parse(raw)) ? undefined : raw
}

function optional(name: string, value: string | null | undefined): Record<string, string> {
  return value ? { [name]: value } : {}
}

/** Jadval holati (URL) → `GET /api/cashdesk/transfers` parametrlari; `to` — exclusive */
export function transferListParams(state: TableState): Record<string, unknown> {
  const filters = state.filters
  const branchId = filters['branchId']?.[0]
  return {
    ...listParams(state, { sortFields: TRANSFER_SORT_FIELDS }),
    ...optional('branchId', branchId && z.string().uuid().safeParse(branchId).success ? branchId : null),
    ...optional('state', pick(filters['state'], TRANSFER_STATES)),
    ...optional('transferType', transferTypeCode(filters['transferType'])),
    ...optional('from', instant(filters['from'])),
    ...optional('to', instant(filters['to'])),
  }
}

const transferTotalsSchema = z.object({
  transfers: z.number(),
  amount: moneySchema,
  byState: z.array(z.object({ state: z.enum(TRANSFER_STATES), transfers: z.number(), amount: moneySchema })),
})

export interface TransferTotals {
  transfers: number
  amount: Money
  byState: readonly { state: TransferState; transfers: number; amount: Money }[]
}

/** Filtrdagi HAMMA topshirish — sahifa emas */
export async function fetchTransferTotals(state: TableState, signal?: AbortSignal): Promise<TransferTotals> {
  const { page: _page, size: _size, sortField: _field, sortOrder: _order, ...filters } = transferListParams(state)
  const response = await api.get<unknown>('/api/cashdesk/transfers/totals', { ...(signal ? { signal } : {}), params: filters })
  const raw = transferTotalsSchema.parse(response.data)
  return {
    transfers: raw.transfers,
    amount: toMoney(raw.amount),
    byState: raw.byState.map((row) => ({ ...row, amount: toMoney(row.amount) })),
  }
}

/** Eksport uchun filtrdagi barcha topshirishlar — sahifama-sahifa, eng ko'pi `limit` ta */
export async function fetchAllTransfers(state: TableState, limit = 5_000): Promise<CashTransfer[]> {
  const rows: CashTransfer[] = []
  for (let page = 1; rows.length < limit; page++) {
    const response = await api.get<unknown>('/api/cashdesk/transfers', {
      params: transferListParams({ ...state, page, pageSize: 100 }),
    })
    const parsed = pageOf(transferSchema).parse(response.data)
    rows.push(...parsed.rows.map(toTransfer))
    if (!parsed.hasNext) break
  }
  return rows.slice(0, limit)
}

export async function fetchTransferPage(state: TableState, signal: AbortSignal): Promise<PageResult<CashTransfer>> {
  const response = await api.get<unknown>('/api/cashdesk/transfers', { signal, params: transferListParams(state) })
  const parsed = pageOf(transferSchema).parse(response.data)
  return {
    items: parsed.rows.map(toTransfer),
    total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
  }
}

export interface TransferDraft {
  branchId: string | null
  amount: number
  transferType: string | null
  externalReference: string | null
  responsiblePerson: string
  remarks: string | null
}

export async function createTransfer(draft: TransferDraft, idempotencyKey: string): Promise<CashTransfer> {
  const response = await api.post<unknown>(
    '/api/cashdesk/transfers',
    {
      ...(draft.branchId ? { branchId: draft.branchId } : {}),
      amount: draft.amount,
      ...(draft.transferType ? { transferType: draft.transferType } : {}),
      ...(draft.externalReference ? { externalReference: draft.externalReference } : {}),
      responsiblePerson: draft.responsiblePerson,
      ...(draft.remarks ? { remarks: draft.remarks } : {}),
    },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return toTransfer(transferSchema.parse(response.data))
}

export async function confirmTransfer(id: string, note: string | null): Promise<CashTransfer> {
  const response = await api.post<unknown>(`/api/cashdesk/transfers/${id}/confirm`, note ? { note } : {})
  return toTransfer(transferSchema.parse(response.data))
}

export async function rejectTransfer(id: string, reason: string): Promise<CashTransfer> {
  const response = await api.post<unknown>(`/api/cashdesk/transfers/${id}/reject`, { reason })
  return toTransfer(transferSchema.parse(response.data))
}

// --- Kassa yozuvlari ---------------------------------------------------------

const entrySchema = z.object({
  id: uuidSchema,
  kind: z.enum(ENTRY_KINDS),
  state: z.enum(ENTRY_STATES),
  branch: branchRefSchema,
  amount: moneySchema,
  category: z.string().nullish(),
  reason: z.string(),
  requestedBy: actorRefSchema,
  requestedAt: instantSchema,
  decidedBy: actorRefSchema.nullish(),
  decidedAt: instantSchema.nullish(),
  decisionNote: z.string().nullish(),
  ledgerTxnId: z.number().nullish(),
  reversedBy: actorRefSchema.nullish(),
  reversedAt: instantSchema.nullish(),
  reverseReason: z.string().nullish(),
  attachmentCount: z.number(),
})

export interface CashEntry {
  id: string
  kind: EntryKind
  state: EntryState
  branch: BranchRef
  amount: Money
  category: string | null
  reason: string
  requestedBy: ActorRef
  requestedAt: string
  decidedBy: ActorRef | null
  decidedAt: string | null
  decisionNote: string | null
  ledgerTxnId: number | null
  reversedBy: ActorRef | null
  reversedAt: string | null
  reverseReason: string | null
  attachmentCount: number
}

function toEntry(raw: z.infer<typeof entrySchema>): CashEntry {
  return {
    ...raw,
    amount: toMoney(raw.amount),
    category: raw.category ?? null,
    decidedBy: raw.decidedBy ?? null,
    decidedAt: raw.decidedAt ?? null,
    decisionNote: raw.decisionNote ?? null,
    ledgerTxnId: raw.ledgerTxnId ?? null,
    reversedBy: raw.reversedBy ?? null,
    reversedAt: raw.reversedAt ?? null,
    reverseReason: raw.reverseReason ?? null,
  }
}

export interface EntryFilter extends ListFilter<EntryState> {
  /** `to` — exclusive */
  from: string | null
  to: string | null
}

export function fetchEntryPage(
  filter: EntryFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<CashEntry>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/cashdesk/entries', {
      signal,
      params: {
        ...listParams(state, { sortFields: ENTRY_SORT_FIELDS }),
        ...optional('branchId', filter.branchId),
        ...optional('state', filter.state),
        ...optional('from', filter.from),
        ...optional('to', filter.to),
      },
    })
    const parsed = pageOf(entrySchema).parse(response.data)
    return {
      items: parsed.rows.map(toEntry),
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

export interface EntryDraft {
  branchId: string | null
  kind: EntryKind
  amount: number
  expenseAccountId: string | null
  reason: string
}

const slipCompanySchema = z.object({ name: z.string().nullish(), logoUrl: z.string().nullish() })

/** Topshirish kvitansiyasi sarlavhasi — `settings.read` siz rollar uchun ham */
export async function fetchSlipCompany(signal?: AbortSignal): Promise<{ name: string | null; logoUrl: string | null }> {
  const response = await api.get<unknown>('/api/cashdesk/transfers/slip-company', { ...(signal ? { signal } : {}) })
  const parsed = slipCompanySchema.parse(response.data)
  return { name: parsed.name ?? null, logoUrl: parsed.logoUrl ?? null }
}

const expenseAccountSchema = z.object({ id: uuidSchema, code: z.string(), name: z.string() })

export type ExpenseAccount = z.infer<typeof expenseAccountSchema>

/** Shu filial kassasida tanlanadigan xarajat turlari (ADR-048, ADR-050) */
export async function fetchExpenseAccounts(branchId: string, signal?: AbortSignal): Promise<readonly ExpenseAccount[]> {
  const response = await api.get<unknown>('/api/cashdesk/entries/expense-accounts', {
    params: { branchId },
    ...(signal ? { signal } : {}),
  })
  return z.array(expenseAccountSchema).parse(response.data)
}

export async function createEntry(draft: EntryDraft, idempotencyKey: string): Promise<CashEntry> {
  const response = await api.post<unknown>(
    '/api/cashdesk/entries',
    {
      ...(draft.branchId ? { branchId: draft.branchId } : {}),
      kind: draft.kind,
      amount: draft.amount,
      ...(draft.expenseAccountId ? { expenseAccountId: draft.expenseAccountId } : {}),
      reason: draft.reason,
    },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return toEntry(entrySchema.parse(response.data))
}

export type EntryDecision = 'approve' | 'reject' | 'reverse'

export async function decideEntry(id: string, decision: EntryDecision, text: string | null): Promise<CashEntry> {
  const body = decision === 'approve' ? (text ? { note: text } : {}) : { reason: text ?? '' }
  const response = await api.post<unknown>(`/api/cashdesk/entries/${id}/${decision}`, body)
  return toEntry(entrySchema.parse(response.data))
}

// --- Bosh ofis sozlamasi -----------------------------------------------------

const settingsSchema = z.object({
  headOffice: z
    .object({
      id: uuidSchema,
      code: z.string(),
      name: z.string(),
      state: z.enum(ORG_NODE_STATES),
      costCenter: z.boolean(),
    })
    .nullish(),
  configured: z.boolean(),
})

export interface CashSettings {
  headOffice: { id: string; code: string; name: string; state: string; costCenter: boolean } | null
  configured: boolean
}

export async function fetchCashSettings(signal?: AbortSignal): Promise<CashSettings> {
  const response = await api.get<unknown>('/api/cashdesk/settings', { ...(signal ? { signal } : {}) })
  const parsed = settingsSchema.parse(response.data)
  return { headOffice: parsed.headOffice ?? null, configured: parsed.configured }
}

export async function saveCashSettings(headOfficeNodeId: string): Promise<CashSettings> {
  const response = await api.put<unknown>('/api/cashdesk/settings', { headOfficeNodeId })
  const parsed = settingsSchema.parse(response.data)
  return { headOffice: parsed.headOffice ?? null, configured: parsed.configured }
}
